import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../services/api';

const IDLE_RESET_MS = 30_000; // auto-reset after 30s on success screen

const Kiosk = () => {
  const [searchParams] = useSearchParams();
  const officeCode = searchParams.get('office')?.toUpperCase() || '';

  const [step,         setStep]         = useState('form');    // 'form' | 'success' | 'invalid'
  const [officeName,   setOfficeName]   = useState('');
  const [verifying,    setVerifying]    = useState(true);
  const [submitting,   setSubmitting]   = useState(false);
  const [result,       setResult]       = useState(null);
  const [errors,       setErrors]       = useState({});
  const [countdown,    setCountdown]    = useState(IDLE_RESET_MS / 1000);
  const countdownRef = useRef(null);

  const emptyForm = { visitorName: '', visitorPhone: '', visitorAddress: '', visitorCompany: '', toWhomMeet: '', purpose: 'Official', purposeDetails: '', department: '' };
  const [form, setForm] = useState(emptyForm);

  // Verify office code on load
  useEffect(() => {
    if (!officeCode) { setVerifying(false); setStep('invalid'); return; }
    api.get(`/offices?code=${officeCode}`).then(({ data }) => {
      const office = data.offices?.find(o => o.code === officeCode);
      if (office) { setOfficeName(office.name); setVerifying(false); }
      else { setVerifying(false); setStep('invalid'); }
    }).catch(() => { setVerifying(false); setStep('invalid'); });
  }, [officeCode]);

  // Countdown and auto-reset after success
  useEffect(() => {
    if (step !== 'success') return;
    setCountdown(IDLE_RESET_MS / 1000);
    countdownRef.current = setInterval(() => {
      setCountdown(c => {
        if (c <= 1) { clearInterval(countdownRef.current); resetForm(); return 0; }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(countdownRef.current);
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  const resetForm = () => {
    setForm(emptyForm);
    setErrors({});
    setResult(null);
    setStep('form');
  };

  const validate = () => {
    const e = {};
    if (!form.visitorName.trim())  e.visitorName  = 'Name is required';
    if (!/^\d{10}$/.test(form.visitorPhone)) e.visitorPhone = 'Enter 10-digit mobile number';
    if (!form.toWhomMeet.trim())   e.toWhomMeet   = 'Please enter who you are meeting';
    if (!form.purpose)             e.purpose      = 'Please select purpose';
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post('/visits/kiosk', { officeCode, ...form });
      setResult(data.visit);
      setStep('success');
    } catch (err) {
      setErrors({ submit: err.response?.data?.message || 'Registration failed. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  const field = (key) => ({
    value: form[key],
    onChange: e => { setForm(f => ({ ...f, [key]: e.target.value })); setErrors(er => ({ ...er, [key]: '' })); },
  });

  if (verifying) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ background: 'linear-gradient(135deg, #065a30 0%, #086F3C 60%, #0a8a4e 100%)' }}>
        <div className="text-center text-white">
          <span className="spinner-border mb-3" style={{ width: 48, height: 48 }}></span>
          <p>Verifying office…</p>
        </div>
      </div>
    );
  }

  if (step === 'invalid') {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ background: 'linear-gradient(135deg, #065a30 0%, #086F3C 60%, #0a8a4e 100%)' }}>
        <div className="card border-0 shadow-lg rounded-4 p-5 text-center" style={{ maxWidth: 420 }}>
          <i className="bi bi-exclamation-triangle-fill text-warning fs-1 mb-3"></i>
          <h4 className="fw-bold">Invalid Office</h4>
          <p className="text-muted">The office code <strong>{officeCode}</strong> was not found or is inactive.</p>
          <p className="text-muted small">Please ask the security guard for the correct kiosk URL.</p>
        </div>
      </div>
    );
  }

  if (step === 'success') {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center p-3" style={{ background: 'linear-gradient(135deg, #065a30 0%, #086F3C 60%, #0a8a4e 100%)' }}>
        <div className="card border-0 shadow-lg rounded-4 p-5 text-center" style={{ maxWidth: 480, width: '100%' }}>
          <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-4"
            style={{ width: 80, height: 80, background: '#e8f5ee' }}>
            <i className="bi bi-check2-circle text-success" style={{ fontSize: '2.5rem' }}></i>
          </div>
          <h3 className="fw-bold mb-1">Welcome!</h3>
          <h5 className="text-muted mb-4">You're checked in at <strong className="text-success">{officeName}</strong></h5>

          <div className="card bg-light border-0 rounded-3 p-3 text-start mb-4">
            <div className="d-flex justify-content-between align-items-center small mb-2">
              <span className="text-muted">Serial No</span>
              <strong className="font-monospace">{result?.serialNumber}</strong>
            </div>
            <div className="d-flex justify-content-between align-items-center small mb-2">
              <span className="text-muted">Name</span>
              <strong>{result?.visitor?.name}</strong>
            </div>
            <div className="d-flex justify-content-between align-items-center small mb-2">
              <span className="text-muted">Meeting</span>
              <strong>{result?.toWhomMeet}</strong>
            </div>
            <div className="d-flex justify-content-between align-items-center small">
              <span className="text-muted">Time In</span>
              <strong>{new Date(result?.timeIn).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</strong>
            </div>
          </div>

          <div className="text-muted small mb-4">
            Please wait for your host. The guard will be notified.<br />
            <strong>This screen will reset in {countdown}s</strong>
          </div>

          <button className="btn btn-success fw-semibold rounded-3 px-5" onClick={resetForm}>
            <i className="bi bi-arrow-left-circle me-2"></i>New Registration
          </button>
        </div>
      </div>
    );
  }

  // Form step
  return (
    <div className="min-vh-100 d-flex flex-column" style={{ background: 'linear-gradient(135deg, #065a30 0%, #086F3C 60%, #0a8a4e 100%)' }}>
      {/* Kiosk Header */}
      <div className="py-4 px-4 d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center gap-3">
          <div style={{ background: '#fff', borderRadius: 10, padding: '4px 8px' }}>
            <img src="/think-gas-logo.png" alt="Think Gas" style={{ height: 32, width: 32, objectFit: 'contain', display: 'block' }} />
          </div>
          <div style={{ lineHeight: 1.2 }}>
            <div style={{ color: '#fff', fontWeight: 800, fontSize: '1.1rem', letterSpacing: '0.05em' }}>THINK GAS</div>
            <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.65rem', letterSpacing: '0.12em', textTransform: 'uppercase' }}>Visitor Self-Registration</div>
          </div>
        </div>
        <div className="text-end">
          <div className="d-flex align-items-center gap-2">
            <i className="bi bi-geo-alt-fill text-warning"></i>
            <div style={{ lineHeight: 1.2 }}>
              <div style={{ color: '#fff', fontWeight: 700, fontSize: '0.9rem' }}>{officeName}</div>
              <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{officeCode}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Form Card */}
      <div className="flex-grow-1 d-flex align-items-center justify-content-center px-3 pb-4">
        <div className="card border-0 shadow-lg rounded-4 w-100" style={{ maxWidth: 560 }}>
          <div className="card-body p-4 p-lg-5">
            <h5 className="fw-bold mb-1 text-center">Visitor Registration</h5>
            <p className="text-muted small text-center mb-4">Please fill in your details to check in</p>

            {errors.submit && (
              <div className="alert alert-danger rounded-3 small py-2">{errors.submit}</div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="row g-3">
                <div className="col-12">
                  <label className="form-label small fw-semibold">Full Name *</label>
                  <input className={`form-control form-control-lg rounded-3 ${errors.visitorName ? 'is-invalid' : ''}`}
                    placeholder="Enter your full name" {...field('visitorName')} />
                  {errors.visitorName && <div className="invalid-feedback">{errors.visitorName}</div>}
                </div>

                <div className="col-md-6">
                  <label className="form-label small fw-semibold">Mobile Number *</label>
                  <input className={`form-control form-control-lg rounded-3 ${errors.visitorPhone ? 'is-invalid' : ''}`}
                    placeholder="10-digit mobile" type="tel" maxLength={10} {...field('visitorPhone')} />
                  {errors.visitorPhone && <div className="invalid-feedback">{errors.visitorPhone}</div>}
                </div>

                <div className="col-md-6">
                  <label className="form-label small fw-semibold">Company (optional)</label>
                  <input className="form-control form-control-lg rounded-3" placeholder="Your company name" {...field('visitorCompany')} />
                </div>

                <div className="col-12">
                  <label className="form-label small fw-semibold">Address (optional)</label>
                  <input className="form-control rounded-3" placeholder="Your address" {...field('visitorAddress')} />
                </div>

                <div className="col-md-6">
                  <label className="form-label small fw-semibold">Whom to Meet *</label>
                  <input className={`form-control form-control-lg rounded-3 ${errors.toWhomMeet ? 'is-invalid' : ''}`}
                    placeholder="Employee / Department name" {...field('toWhomMeet')} />
                  {errors.toWhomMeet && <div className="invalid-feedback">{errors.toWhomMeet}</div>}
                </div>

                <div className="col-md-6">
                  <label className="form-label small fw-semibold">Department (optional)</label>
                  <input className="form-control form-control-lg rounded-3" placeholder="e.g. HR, Finance" {...field('department')} />
                </div>

                <div className="col-md-6">
                  <label className="form-label small fw-semibold">Purpose *</label>
                  <select className={`form-select form-select-lg rounded-3 ${errors.purpose ? 'is-invalid' : ''}`} {...field('purpose')}>
                    <option value="Official">Official</option>
                    <option value="Personal">Personal</option>
                  </select>
                  {errors.purpose && <div className="invalid-feedback">{errors.purpose}</div>}
                </div>

                <div className="col-md-6">
                  <label className="form-label small fw-semibold">Purpose Details (optional)</label>
                  <input className="form-control form-control-lg rounded-3" placeholder="Brief description" {...field('purposeDetails')} />
                </div>

                <div className="col-12 mt-2">
                  <button
                    type="submit"
                    className="btn btn-success btn-lg w-100 fw-bold rounded-3"
                    disabled={submitting}
                    style={{ background: '#086F3C', border: 'none', letterSpacing: '0.04em', fontSize: '1.05rem' }}
                  >
                    {submitting
                      ? <><span className="spinner-border spinner-border-sm me-2"></span>Registering…</>
                      : <><i className="bi bi-check2-circle me-2"></i>Check In</>}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      <div className="py-3 text-center" style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.72rem' }}>
        &copy; {new Date().getFullYear()} Think Gas · Visitor Self-Registration Kiosk
      </div>
    </div>
  );
};

export default Kiosk;
